import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import Pusher from 'pusher-js';
import * as PusherTypes from 'pusher-js';
import * as Sentry from '@sentry/react';
import { PusherEvent } from 'pusher-js/types/src/core/connection/protocol/message-types';
import { ALL_RECORD_TYPES, FlagshipModelOptions } from 'constants/recordTypes';
import { useDispatch, useSelector } from 'store';
import _ from 'lodash';
import { apiSlice, ApiTagTypes } from 'store/slices/api';

type PusherContextType = {
  pusherChannel: PusherTypes.Channel;
  jobsChannel: PusherTypes.Channel;
  recordsChannel: PusherTypes.Channel;
};

// @ts-ignore -- Stub out channels in local env w/o Pusher key
const channelStub = {
  bind: () => {},
  bind_global: () => {},
  unbind: () => {},
  unbind_global: () => {},
  unbind_all: () => {},
} as PusherTypes.Channel;

export const PusherContext = createContext({} as PusherContextType);

export const usePusherContext = () => useContext(PusherContext);

const PusherProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { activeOrgId } = useSelector((state) => state.org);
  const pusher = process.env.PUSHER_APP_KEY
    ? new Pusher(process.env.PUSHER_APP_KEY || '', {
        cluster: process.env.PUSHER_APP_CLUSTER || '',
      })
    : null;
  const pusherChannel = pusher?.subscribe('parts') ?? channelStub;
  const jobsChannel = pusher?.subscribe('jobs') ?? channelStub;
  const recordsChannel = pusher?.subscribe('records') ?? channelStub;

  const handleRecordMessage = useCallback(
    async (
      __: PusherEvent,
      eventData:
        | {
            org_id: string;
            id: string;
            action: 'create' | 'update' | 'delete';
            model_name: FlagshipModelOptions;
          }[]
        | {
            org_id: string;
            id: string;
            action: 'create' | 'update' | 'delete';
            model_name: FlagshipModelOptions;
          }
    ) => {
      try {
        const tagsToInvalidate: { type: ApiTagTypes; id: string }[] = [];
        const events = Array.isArray(eventData) ? eventData : [eventData];
        events.forEach((event) => {
          if (event.org_id === activeOrgId) {
            // get the associated record type configuration
            const matchingRecordType = _.chain(ALL_RECORD_TYPES)
              .values()
              .find({ flagshipModel: event.model_name })
              .value();
            if (matchingRecordType && matchingRecordType.tagTypes) {
              // invalidate the associated tags in RTK Query
              tagsToInvalidate.push(
                ..._.flatMap(matchingRecordType.tagTypes, (tag) => [
                  { type: tag, id: event.id },
                  { type: tag, id: 'LIST' },
                  { type: tag, id: 'PARTIAL-LIST' },
                ])
              );
            }
          }
        });
        if (tagsToInvalidate.length > 0) {
          dispatch(
            apiSlice.util.invalidateTags(
              _.uniqBy(tagsToInvalidate, (tag) => tag.type + tag.id)
            )
          );
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [dispatch, activeOrgId]
  );

  const bindChannel = useCallback(() => {
    recordsChannel.bind_global(handleRecordMessage);
  }, [recordsChannel, handleRecordMessage]);

  const unbindChannel = useCallback(() => {
    recordsChannel.unbind_global(handleRecordMessage);
  }, [recordsChannel, handleRecordMessage]);

  useEffect(() => {
    bindChannel();
    return () => {
      unbindChannel();
    };
  }, [bindChannel, unbindChannel]);

  return (
    <PusherContext.Provider
      value={{ pusherChannel, jobsChannel, recordsChannel }}
    >
      {children}
    </PusherContext.Provider>
  );
};

export default PusherProvider;
